/**
 * ProductPricingWorkspace — Gate 1 container.
 *
 * Two-column layout per the design handoff: `.pv2-content` (main cards) and
 * `.pv2-aside` (sticky 340px Pricing Controls). The workspace is the
 * unconditional render for the Product & Pricing tab; there is no legacy
 * fallback.
 *
 * Lock-version correctness: active delivery-lock mode, candidate mode
 * (inspecting a selectable non-active version with the par guard on) and
 * historical inspection overlay lock-derived fields only. Local pricing
 * fields still come from live loan tables so switching lock versions never
 * blanks concessions, lender credit, LOCR, discount, or BPC. In candidate
 * mode the local inputs stay editable and "Set Active & Save" stages them
 * and switches the lock in one server transaction; the lock cannot be set
 * active while the displayed pricing is below par.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LockKeyhole } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useGateLocals } from './hooks/useGateLocals';
import { usePricingRecalc, type PricingRecalcOutput } from './hooks/usePricingRecalc';
import { formatBpsInputValue, investorLoanNumberError, usePricingDraft, type PricingFieldId, type UsePricingDraftOutput } from './hooks/usePricingDraft';
import { useBpcCap } from './hooks/useBpcCap';
import { useLocrEligibility } from './hooks/useLocrEligibility';
import { useGroupedPricingSave, type SaveResult } from './hooks/useGroupedPricingSave';
import { PricingPendingBar } from './PricingPendingBar';
import { LockStatusCard } from './LockStatusCard';
import { LockDetailsCard } from './LockDetailsCard';
import { LockHistoryPicker } from './LockHistoryPicker';
import { KpiStrip } from './KpiStrip';
import { PriceWaterfallLedger } from './PriceWaterfallLedger';
import { ConcessionsStrip } from './ConcessionsStrip';
import { ConcessionsCureModal } from './ConcessionsCureModal';
import { PricingChangeHistoryPanel } from './PricingChangeHistoryPanel';
import { InvestorLockDocsCard } from './InvestorLockDocsCard';
import { PricingControlsAside } from './PricingControlsAside';
import { PricingReasonModal } from './PricingReasonModal';
import { LockVersionBanner } from './SnapshotBanner';
import { DeliveryLockBanner } from './DeliveryLockBanner';
import { ConfirmLockEmailModal } from './ConfirmLockEmailModal';
import { LoanContextRow } from './LoanContextRow';
import { PriceFlowDiagram } from './PriceFlowDiagram';
import { PricingBreakdownLedger } from './PricingBreakdownLedger';
import { computeMaterialLockHash, type LockVersion } from '@/services/loanPricingService';
import type { DeliveryLockSelection } from '@/services/deliveryLockService';
import type { ProspectLoan } from '../types';
import { toNumberOrNull } from '../utils/loanValueHelpers';
import { extractEncompassLoCompBps } from '@/utils/loCompDerived';
import { supabase } from '@/utils/supabase';
import { formatCurrency } from './format';
import { formatDollarInputValue } from './inputFormat';
import { checkLenderCreditCap, checkLocrCap } from './pricingCapValidation';
import { getReviewableMaterialRuns, hasNewerMaterialRun } from './lockMaterialRuns';
import './styles.css';

const EMPTY_LOCK_VERSIONS: LockVersion[] = [];
// Concession removals are judged on the saved pricing of the ACTIVE lock; while a
// candidate is on screen that basis is not the one displayed, so removals wait.
const CANDIDATE_CONCESSION_DELETE_REASON =
  'Set this lock active first — removing a concession applies to the active lock.';
const QUEUED_SAVE_REASON =
  'Another save is queued for this loan. Wait for it to finish or cancel it before saving these changes.';

interface SubmittedPricingSnapshot {
  groupId: string;
  startedAt: string;
  values: Partial<Record<PricingFieldId, string>>;
  overrides: UsePricingDraftOutput['recalcOverrides'];
  encompassKeyAtStart: string;
  locrKeyAtStart: string;
}

interface PriceFinalizedPreviewRow {
  label: string;
  beforeValue: string;
  beforeAmountValue?: string;
  afterValue: string;
  afterAmountValue?: string;
  note?: string;
  changed: boolean;
}

interface PriceFinalizedPreviewRecipients {
  to: string[];
  cc: string[];
  loading: boolean;
}

export interface ProductPricingWorkspaceProps {
  loan: ProspectLoan;
  currentLoan?: ProspectLoan;
  /**
   * Un-overlaid `encompass_loans` row when any overlay (snapshot view OR
   * delivery-lock mode) is active. Required for divergence detection — the
   * `loan` prop carries the overlaid view so comparing it to the active
   * selection's effective_* fields would always match.
   */
  liveLoan?: ProspectLoan;
  onLoanUpdate?: (updates: Partial<ProspectLoan>) => void;
  onAuthoritativeRefresh?: () => Promise<void> | void;
  lockVersions?: LockVersion[];
  selectedLockVersionId?: string | null;
  onLockVersionSelect?: (id: string | null) => void;
  /** @deprecated transitional aliases while callers/tests move to lock versions. */
  snapshots?: LockVersion[];
  selectedSnapshotId?: string | null;
  onSnapshotSelect?: (id: string | null) => void;
  isViewingSnapshot?: boolean;
  deliveryLockSelection?: DeliveryLockSelection | null;
  isDeliveryLockMode?: boolean;
  onUseLockVersionAsDeliveryLock?: (version: LockVersion) => Promise<void> | void;
  /** @deprecated transitional alias while callers/tests move to lock versions. */
  onUseSnapshotAsDeliveryLock?: (version: LockVersion) => Promise<void> | void;
  /**
   * Candidate mode: the selectable non-active lock version being inspected
   * (the row Set Active will activate). Local pricing stays editable and the
   * pending bar's primary action becomes "Set Active & Save". Null outside
   * candidate mode; the parent only sets it while the par guard flag is on.
   */
  candidateVersion?: LockVersion | null;
  /** `pricing_set_active_par_guard` resolved by the parent; false = legacy behaviour. */
  parGuardEnabled?: boolean;
  /**
   * Called after every candidate-mode save attempt (success or error) with
   * the target lock version id; the parent re-reads the active selection and
   * leaves candidate mode only when that target is confirmed active.
   */
  onActivated?: (targetLockVersionId: string) => Promise<void> | void;
  /** Reports whether the page holds unsaved edits, so the sheet can guard leaving. */
  onDirtyChange?: (dirty: boolean) => void;
}

const STALE_PENDING_MS = 15_000;

function makeDiffClass(isViewing: boolean, currentLoan?: ProspectLoan) {
  const canon = (v: unknown): string | null => {
    if (v === null || v === undefined) {
      return null;
    }
    const s = String(v).split('T')[0];
    const n = Number(s);
    return !isNaN(n) ? String(n) : s;
  };
  return (snapshotVal: unknown, currentVal: unknown): string => {
    if (!isViewing || !currentLoan) {
      return '';
    }
    if (canon(snapshotVal) === canon(currentVal)) {
      return '';
    }
    return 'text-red-600 font-semibold';
  };
}

function formatPercentInputValue(value: number): string {
  return value.toFixed(3).replace(/\.?0+$/, '');
}

function locrKeyFromItems(loanId: string, pricingItems: LoanPricingItem[]): string {
  const locr = pricingItems.find((p) => p.itemType === 'lo_compensation');
  return JSON.stringify({
    loanId,
    locrBps: toNumberOrNull(locr?.reductionBps ?? null),
    updatedAt: locr?.updatedAt ?? null
  });
}

/**
 * Has the authoritative row moved on a submitted Encompass-backed field? Only the
 * pricing VALUES count: `encompass_loans.updated_at` also moves for file-lock
 * metadata alone (someone opening the loan), which is not a readback and must
 * not release the submitted snapshot while the row still shows pre-save values.
 */
function encompassPricingValuesChanged(currentEncompassKey: string, keyAtStart: string): boolean {
  const strip = (key: string): string => {
    try {
      const { updatedAt: _updatedAt, ...values } = JSON.parse(key) as Record<string, unknown>;
      return JSON.stringify(values);
    } catch {
      return key;
    }
  };
  return strip(currentEncompassKey) !== strip(keyAtStart);
}

function submittedSnapshotHasAuthoritativeRefresh(
  snapshot: SubmittedPricingSnapshot,
  currentEncompassKey: string,
  currentLocrKey: string
): boolean {
  const changedFields = Object.keys(snapshot.values) as PricingFieldId[];
  const hasEncompassBackedField = changedFields.some((id) => id !== 'lo_comp_reduction_bps');
  const hasLocrOnly = changedFields.length > 0 && changedFields.every((id) => id === 'lo_comp_reduction_bps');

  if (hasEncompassBackedField) {
    return encompassPricingValuesChanged(currentEncompassKey, snapshot.encompassKeyAtStart);
  }
  if (hasLocrOnly) {
    return currentLocrKey !== snapshot.locrKeyAtStart;
  }
  return encompassPricingValuesChanged(currentEncompassKey, snapshot.encompassKeyAtStart)
    || currentLocrKey !== snapshot.locrKeyAtStart;
}

function makePreviewRow(
  label: string,
  beforeValue: string,
  afterValue: string,
  note?: string,
  beforeAmountValue?: string,
  afterAmountValue?: string
): PriceFinalizedPreviewRow {
  return {
    label,
    beforeValue,
    beforeAmountValue,
    afterValue,
    afterAmountValue,
    note,
    changed: beforeValue !== afterValue || beforeAmountValue !== afterAmountValue
  };
}

function formatLocrBps(recalc: PricingRecalcOutput): string {
  return `${(recalc.locrPoints * 100).toFixed(2).replace(/\.00$/, '')} bps`;
}

function formatLocrDollars(recalc: PricingRecalcOutput): string {
  const locrBps = recalc.locrPoints * 100;
  return formatCurrency(recalc.loanAmount * (locrBps / 10000));
}

function buildPriceFinalizedPreviewRows(
  before: PricingRecalcOutput,
  after: PricingRecalcOutput
): PriceFinalizedPreviewRow[] {
  return [
    makePreviewRow(
      'Discount points',
      `${before.discountPoints.toFixed(3)}%`,
      `${after.discountPoints.toFixed(3)}%`,
      undefined,
      formatCurrency(before.discountPoints * before.loanAmount * 0.01),
      formatCurrency(after.discountPoints * after.loanAmount * 0.01)
    ),
    makePreviewRow('Borrower-paid comp', `${(before.bpcBps / 100).toFixed(3)}%`, `${(after.bpcBps / 100).toFixed(3)}%`),
    makePreviewRow('Lender credit (4794)', '', '', undefined, formatCurrency(before.lenderCreditField4794Dollars), formatCurrency(after.lenderCreditField4794Dollars)),
    makePreviewRow('Lender-paid closing costs', '', '', undefined, formatCurrency(before.lenderPaidClosingCostDollars), formatCurrency(after.lenderPaidClosingCostDollars)),
    makePreviewRow('Total lender credit', '', '', undefined, formatCurrency(before.lenderCreditDollars), formatCurrency(after.lenderCreditDollars)),
    makePreviewRow('LO comp reduction', formatLocrBps(before), formatLocrBps(after), undefined, formatLocrDollars(before), formatLocrDollars(after)),
    makePreviewRow('Net LO locked price', before.netLoLocked.toFixed(3), after.netLoLocked.toFixed(3)),
    makePreviewRow('Investor lock price', before.investorLockPrice.toFixed(3), after.investorLockPrice.toFixed(3)),
    makePreviewRow('Company profit', '', '', undefined, formatCurrency(before.companyProfitDollars), formatCurrency(after.companyProfitDollars))
  ];
}

function parseSavePayloadNumber(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  const n = Number(String(value).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function buildSubmittedRecalcOverrides(
  fields: Array<{ fieldId: string; value: string | null }>
): UsePricingDraftOutput['recalcOverrides'] {
  const overrides: UsePricingDraftOutput['recalcOverrides'] = {};
  for (const field of fields) {
    const parsed = parseSavePayloadNumber(field.value);
    switch (field.fieldId) {
      case 'NEWHUD.X1150':
        overrides.discountPointPercent = parsed;
        break;
      case '388':
        overrides.bpcPercent = parsed;
        break;
      case '4794':
        overrides.lenderCredit4794DollarsOverride = parsed ?? 0;
        break;
      case 'LW.LO_REDUCTION':
        overrides.locrBps = parsed;
        break;
    }
  }
  return overrides;
}

export function ProductPricingWorkspace({
  loan,
  currentLoan,
  liveLoan,
  onLoanUpdate,
  onAuthoritativeRefresh,
  lockVersions = [],
  selectedLockVersionId = null,
  onLockVersionSelect,
  snapshots: legacySnapshots,
  selectedSnapshotId: legacySelectedSnapshotId,
  onSnapshotSelect: legacyOnSnapshotSelect,
  isViewingSnapshot = false,
  deliveryLockSelection = null,
  isDeliveryLockMode = false,
  onUseLockVersionAsDeliveryLock,
  onUseSnapshotAsDeliveryLock: legacyOnUseSnapshotAsDeliveryLock,
  candidateVersion = null,
  parGuardEnabled = false,
  onActivated,
  onDirtyChange
}: ProductPricingWorkspaceProps) {
  const resolvedLockVersions = useMemo(
    () => (lockVersions.length > 0 ? lockVersions : legacySnapshots ?? EMPTY_LOCK_VERSIONS),
    [lockVersions, legacySnapshots]
  );
  const resolvedSelectedLockVersionId = selectedLockVersionId ?? legacySelectedSnapshotId ?? null;
  const resolvedOnLockVersionSelect = onLockVersionSelect ?? legacyOnSnapshotSelect;
  const resolvedOnUseLockVersionAsDeliveryLock =
    onUseLockVersionAsDeliveryLock ?? legacyOnUseSnapshotAsDeliveryLock;
  const reconStatus = (
    liveLoan?.recon_status
    ?? currentLoan?.recon_status
    ?? loan.recon_status
  )?.trim().toLowerCase();
  const isTerminalBankerRecon = reconStatus === 'finalized' || reconStatus === 'pushed';
  const isCandidateMode = candidateVersion !== null;
  // Lock-bound children (banner, diffs, picker, lock details, investor docs)
  // treat a candidate like an inspection; the aside, strip and bar do not.
  const inspecting = isViewingSnapshot || isCandidateMode;
  const priceFinalizedEmailSuppressed = isDeliveryLockMode || isCandidateMode;
  const diffClass = makeDiffClass(inspecting, currentLoan);

  const selectedLockVersion = useMemo(
    () => (resolvedSelectedLockVersionId ? resolvedLockVersions.find((s) => s.id === resolvedSelectedLockVersionId) ?? null : null),
    [resolvedSelectedLockVersionId, resolvedLockVersions]
  );
  const reviewableMaterialRuns = useMemo(
    () => getReviewableMaterialRuns(resolvedLockVersions, deliveryLockSelection?.lockVersionId ?? null),
    [resolvedLockVersions, deliveryLockSelection?.lockVersionId]
  );
  const activeMaterialRun = useMemo(
    () => reviewableMaterialRuns.find((run) => run.hasActive) ?? null,
    [reviewableMaterialRuns]
  );

  const { concessions, pricingItems, refreshConcessions, refreshPricingItems } =
    useGateLocals(loan.id, loan.encompass_loan_id);

  // The Investor Loan # baseline always comes from the live loan: candidate and
  // lock-history overlays carry a lock's captured copy, never the current number.
  const liveInvestorLoanNumber = (liveLoan ?? loan).rate_lock_investor_commitment ?? null;
  const draftSourceLoan = useMemo<ProspectLoan>(
    () => ({ ...loan, rate_lock_investor_commitment: liveInvestorLoanNumber ?? undefined }),
    [loan, liveInvestorLoanNumber]
  );
  const draft = usePricingDraft(draftSourceLoan, pricingItems);
  const bpcCap = useBpcCap(loan, loan.loan_officer_id ?? null);
  const locrEligibility = useLocrEligibility(loan);
  const locrBaseline = pricingItems.find((p) => p.itemType === 'lo_compensation')?.reductionBps ?? null;
  const encompassAuthoritativeKey = useMemo(() => JSON.stringify({
    loanId: loan.id,
    updatedAt: loan.updated_at ?? null,
    discountPointPercent: toNumberOrNull(loan.discount_point_percent),
    bpcPercent: toNumberOrNull(loan.origination_fee_percent),
    lenderCredit4794: toNumberOrNull(loan.trans_details_non_specific_lender_credit)
  }), [
    loan.id,
    loan.updated_at,
    loan.discount_point_percent,
    loan.origination_fee_percent,
    loan.trans_details_non_specific_lender_credit
  ]);
  const locrAuthoritativeKey = useMemo(
    () => locrKeyFromItems(loan.id, pricingItems),
    [loan.id, pricingItems]
  );
  const authoritativePricingKey = useMemo(() => JSON.stringify({
    encompass: encompassAuthoritativeKey,
    locr: locrAuthoritativeKey,
    locrBps: toNumberOrNull(locrBaseline),
    // Rebases a clean Investor Loan # when the live number moves. It is not
    // part of the submitted-snapshot comparison, so it never releases a
    // pending pricing readback early.
    investorLoanNumber: (liveInvestorLoanNumber ?? '').trim()
  }), [encompassAuthoritativeKey, locrAuthoritativeKey, locrBaseline, liveInvestorLoanNumber]);
  const [submittedSnapshot, setSubmittedSnapshot] = useState<SubmittedPricingSnapshot | null>(null);

  // Hydrate draft once pricingItems arrives from the async fetch in useGateLocals.
  // usePricingDraft captures initial values ONCE at mount — if pricingItems is still [] at that
  // point, LOCR's `previous` anchors to blank and a later edit would diff against blank instead of
  // the real `loan_pricing_items.reduction_bps`. `rebaseClean` rebases unchanged fields only and
  // preserves dirty fields' in-progress input, so the fix still holds even if the user types into
  // another field before the fetch resolves.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) {
      return;
    }
    if (pricingItems.length === 0) {
      return;
    }
    hydratedRef.current = true;
    draft.rebaseClean(draftSourceLoan, pricingItems);
  }, [pricingItems, draftSourceLoan, draft]);

  // Keep open Product & Pricing tabs aligned with the authoritative loan prop.
  // Clean fields should always pick up Encompass-synced values; dirty fields
  // keep the user's active input but advance their baseline.
  const authoritativePricingKeyRef = useRef(authoritativePricingKey);
  useEffect(() => {
    if (authoritativePricingKeyRef.current === authoritativePricingKey) {
      return;
    }
    authoritativePricingKeyRef.current = authoritativePricingKey;
    if (submittedSnapshot) {
      if (!submittedSnapshotHasAuthoritativeRefresh(
        submittedSnapshot,
        encompassAuthoritativeKey,
        locrAuthoritativeKey
      )) {
        return;
      }
      setSubmittedSnapshot(null);
    }
    draft.rebaseClean(draftSourceLoan, pricingItems);
  }, [
    authoritativePricingKey,
    encompassAuthoritativeKey,
    locrAuthoritativeKey,
    draftSourceLoan,
    pricingItems,
    draft,
    submittedSnapshot
  ]);

  const effectiveRecalcOverrides = useMemo(() => ({
    ...(submittedSnapshot?.overrides ?? {}),
    ...draft.recalcOverrides
  }), [draft.recalcOverrides, submittedSnapshot]);

  const recalc = usePricingRecalc({
    loan,
    pricingItems,
    concessions,
    ...effectiveRecalcOverrides
  });

  // Baseline recalc = authoritative server state for Δ vs. saved in the aside
  // and pending bar. Do not overlay audit/mirror state here; after a save the
  // parent refreshes the authoritative loan row from encompass_loans.
  const baselineRecalc = usePricingRecalc({
    loan,
    pricingItems,
    concessions
  });

  // Shared cap math — derived from `recalc` so the workspace's validation
  // effect, the aside's footer copy, and the hover breakdown all use a single
  // source of truth. Server mirrors this in validateLenderCreditCap using the
  // same formula against staged + DB values.
  const maxTotalLenderCreditDollars = Math.max(
    0,
    recalc.availableLcPts * recalc.loanAmount * 0.01
  );
  const maxGenericLenderCreditDollars = Math.max(
    0,
    maxTotalLenderCreditDollars - recalc.lenderPaidClosingCostDollars
  );

  // BPC client-side cap gate. Dirty-scoped — if the user hasn't touched BPC,
  // the effect clears invalid and exits. Otherwise the loan's pre-existing BPC
  // could fall outside the cap (say, cap was lowered after lock) and this
  // effect would set invalid → draft.anyInvalid → save disabled for entirely
  // unrelated fields (Discount, Lender Credit, LOCR).
  const bpcDraftValue = draft.values.origination_fee_percent.value;
  const bpcDirty = draft.values.origination_fee_percent.changed;
  useEffect(() => {
    if (!bpcDirty) {
      draft.setInvalid('origination_fee_percent', null);
      return;
    }
    const raw = bpcDraftValue.trim();
    if (raw === '') {
      draft.setInvalid('origination_fee_percent', null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) {
      draft.setInvalid('origination_fee_percent', 'Enter a non-negative number');
      return;
    }
    if (!bpcCap.allowEdit) {
      draft.setInvalid('origination_fee_percent', bpcCap.reason);
      return;
    }
    if (bpcCap.capPercent !== null && n > bpcCap.capPercent) {
      draft.setInvalid('origination_fee_percent', `Exceeds comp plan cap of ${bpcCap.capPercent.toFixed(2)}%`);
      return;
    }
    draft.setInvalid('origination_fee_percent', null);
  }, [bpcDirty, bpcDraftValue, bpcCap.allowEdit, bpcCap.capPercent, bpcCap.reason, draft]);

  // Per-field numeric-invalid gate for the three non-BPC fields (2B.0 + QA follow-up).
  // BPC has its own stricter gate above (RESPA / plan-cap). This effect combines:
  //   (a) Basic numeric validation (parseable, non-negative) for all three fields.
  //   (b) Lender Credit cap: payload value must not exceed `maxGenericLenderCreditDollars`
  //       (the client-visible portion of `availableLcPts` after subtracting itemized LENPCC).
  //   (c) LOCR cap: payload value must not exceed the built-in LO Compensation in basis
  //       points (`extractEncompassLoCompBps` over the loan's rate_lock_buy_side_adjustments).
  //
  // All three fields are validated in a SINGLE effect rather than one per field, so
  // competing effects never write conflicting `invalid` values on the same commit cycle.
  // Feeds `draft.anyInvalid` → disables Save via PricingPendingBar. Server (2B.4 +
  // today's follow-up) re-validates authoritatively before any DB mutation.
  const discountRaw = draft.values.discount_point_percent.value;
  const lenderCreditRaw = draft.values.lender_credit_4794_dollars.value;
  const locrRaw = draft.values.lo_comp_reduction_bps.value;
  const lenderCreditDirty = draft.values.lender_credit_4794_dollars.changed;
  const locrDirty = draft.values.lo_comp_reduction_bps.changed;
  const builtInLoCompBps = useMemo(
    () => extractEncompassLoCompBps(loan.rate_lock_buy_side_adjustments),
    [loan.rate_lock_buy_side_adjustments]
  );
  useEffect(() => {
    const toCleanNumber = (raw: string): number | null => {
      const cleaned = raw.replace(/[$,\s]/g, '');
      if (cleaned === '') {
        return null;
      }
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : NaN; // NaN signals unparseable; null signals blank
    };
    const basicInvalid = (n: number | null): string | null => {
      if (n === null) {
        return null;
      }
      if (Number.isNaN(n)) {
        return 'Enter a valid number';
      }
      if (n < 0) {
        return 'Must be non-negative';
      }
      return null;
    };

    // Discount — numeric only (no cap gate today).
    const dN = toCleanNumber(discountRaw);
    draft.setInvalid('discount_point_percent', basicInvalid(dN));

    // Lender Credit — numeric → generic-cap enforcement when dirty.
    const lcN = toCleanNumber(lenderCreditRaw);
    const lcBasic = basicInvalid(lcN);
    if (lcBasic !== null) {
      draft.setInvalid('lender_credit_4794_dollars', lcBasic);
    } else {
      draft.setInvalid(
        'lender_credit_4794_dollars',
        checkLenderCreditCap(lcN ?? NaN, maxGenericLenderCreditDollars, lenderCreditDirty)
      );
    }

    // LOCR — numeric → built-in-LO-comp cap enforcement when dirty. Eligibility
    // (is there any LO comp to reduce?) is handled separately by useLocrEligibility;
    // this gate only fires for over-cap when a built-in amount exists. In
    // candidate mode the carried-over value is checked too: a reduction the
    // new lock cannot absorb must be corrected before Set Active & Save.
    const locrN = toCleanNumber(locrRaw);
    const locrBasic =
      locrDirty && locrN === null ? 'LO comp reduction cannot be blank' : basicInvalid(locrN);
    if (locrBasic !== null) {
      draft.setInvalid('lo_comp_reduction_bps', locrBasic);
    } else {
      draft.setInvalid(
        'lo_comp_reduction_bps',
        checkLocrCap(locrN ?? NaN, builtInLoCompBps, locrDirty || isCandidateMode)
      );
    }
  }, [
    discountRaw,
    lenderCreditRaw,
    locrRaw,
    lenderCreditDirty,
    locrDirty,
    isCandidateMode,
    maxGenericLenderCreditDollars,
    builtInLoCompBps,
    draft
  ]);

  // Investor Loan # entry rule (text): clearing an existing number or going
  // over the Encompass limit is an error; an untouched value never is.
  const investorDraftValue = draft.values.investor_loan_number.value;
  const investorDraftPrevious = draft.values.investor_loan_number.previous;
  useEffect(() => {
    draft.setInvalid('investor_loan_number', investorLoanNumberError(investorDraftValue, investorDraftPrevious));
  }, [investorDraftValue, investorDraftPrevious, draft]);

  // A save that changes only the Investor Loan # carries no pricing: it skips
  // the pricing reason, the price-finalized email and the par/profit checks.
  const pricingDirty = draft.dirtyFields.some((id) => id !== 'investor_loan_number');
  const investorOnlyDirty = draft.dirtyCount > 0 && !pricingDirty;
  const saveFieldsInvalid = investorOnlyDirty
    ? draft.values.investor_loan_number.invalid !== null
    : draft.anyInvalid;

  // An entry typed while a save is sending counts too, even when it matches the
  // old baseline: the save carries only what it sent.
  const hasUnsavedEdits = draft.dirtyCount > 0 || draft.hasUnsentEdits;
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(hasUnsavedEdits);
  }, [hasUnsavedEdits]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  // Grouped save hook + success toast. A candidate-mode save owns its own
  // outcome toast (one toast per outcome), so the generic one is suppressed
  // for the save that set the ref.
  const suppressValidatedToastRef = useRef(false);
  const validatedToastCopyRef = useRef('Pricing sent to Encompass');
  const grouped = useGroupedPricingSave({
    loanId: loan.id,
    encompassLoanId: loan.encompass_loan_id ?? '',
    onValidated: useCallback(() => {
      if (suppressValidatedToastRef.current) {
        suppressValidatedToastRef.current = false;
        return;
      }
      toast.success(validatedToastCopyRef.current);
    }, [])
  });

  const refreshAuthoritativePricing = useCallback(async () => {
    if (!onAuthoritativeRefresh) {
      return;
    }
    try {
      await onAuthoritativeRefresh();
    } catch (error) {
      console.warn('[ProductPricingWorkspace] Failed to refresh authoritative loan row', error);
    }
  }, [onAuthoritativeRefresh]);

  const activeGroupBlocksNewSave =
    grouped.activeGroup !== null
    && ['pending', 'pushing', 'waiting_unlock', 'waiting_retry'].includes(
      grouped.activeGroup.status
    );
  const submittedSnapshotBlocksNewSave = submittedSnapshot !== null;

  // Par guard surfaces. The shortfall uses the same 3-decimal rounding as the
  // gate (`recalc.belowPar`) and the waterfall row, so all three agree.
  const displayedNetLoLocked = Number(recalc.netLoLocked.toFixed(3));
  const parShortfall = parGuardEnabled && recalc.belowPar
    ? {
        points: Number((100 - displayedNetLoLocked).toFixed(3)),
        dollars: Math.abs(recalc.companyProfitDollars)
      }
    : null;
  // Copy variant: Set Active is the pending action unless the lock is already active.
  const setActivePending = !isDeliveryLockMode;
  const headerBlockedReason = parGuardEnabled && recalc.belowPar
    ? 'Below par — cover the shortfall first'
    : undefined;
  const allBlockedReason = parGuardEnabled && (activeGroupBlocksNewSave || submittedSnapshotBlocksNewSave)
    ? 'Finish the pending pricing save first'
    : undefined;
  // Concession removals are judged on the saved pricing of the ACTIVE lock
  // (`baselineRecalc`). That basis is unusable while a candidate is on screen
  // (the displayed lock is not the active one) and from the moment a save is
  // sent until Encompass has read it back (the saved row still shows the
  // pre-save values).
  const concessionDeleteBlockedReason = !parGuardEnabled
    ? null
    : isCandidateMode
      ? CANDIDATE_CONCESSION_DELETE_REASON
      : grouped.isSaving || activeGroupBlocksNewSave || submittedSnapshotBlocksNewSave
        ? 'Finish the pending pricing save first'
        : null;
  const belowParLine = parShortfall
    ? `Below par by ${parShortfall.points.toFixed(3)} pts — cover the shortfall in Pricing controls.`
    : null;
  const lockHolderName = (liveLoan ?? loan).file_locked_by_name || 'someone';
  const activationWaitingLabel =
    `Saved here. Active delivery lock updated. Waiting for ${lockHolderName} to exit the loan — Encompass updates when it's free.`;
  const investorWaitingLabel =
    `Saved in the portal. Waiting for ${lockHolderName} to close the loan in Encompass.`;
  const activeGroupSwitchedLock = Boolean(grouped.activeGroup?.activateLockVersionId);
  const activeGroupInvestorOnly = grouped.activeGroup?.containsPricingChanges === false;

  // A `pending` group older than STALE_PENDING_MS was staged but never
  // claimed by the worker (the claim failed after the commit); offer Retry.
  const [pendingStale, setPendingStale] = useState(false);
  const pendingGroupUpdatedAt = grouped.activeGroup?.status === 'pending' ? grouped.activeGroup.updatedAt : null;
  useEffect(() => {
    if (!pendingGroupUpdatedAt) {
      setPendingStale(false);
      return;
    }
    const age = Date.now() - Date.parse(pendingGroupUpdatedAt);
    if (!Number.isFinite(age)) {
      setPendingStale(false);
      return;
    }
    if (age >= STALE_PENDING_MS) {
      setPendingStale(true);
      return;
    }
    setPendingStale(false);
    const timer = window.setTimeout(() => setPendingStale(true), STALE_PENDING_MS - age);
    return () => window.clearTimeout(timer);
  }, [pendingGroupUpdatedAt]);

  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonDraft, setReasonDraft] = useState('');
  const [priceFinalizedEmailRequested, setPriceFinalizedEmailRequested] = useState(true);
  const [confirmLockEmailOpen, setConfirmLockEmailOpen] = useState(false);
  const [lockConfirmationLastSent, setLockConfirmationLastSent] = useState<
    { sentAt: string; sentByEmail: string | null; resendId: string | null } | null
  >(null);
  const lockSectionRef = useRef<HTMLDivElement | null>(null);

  // "Newer lock version available" cue. Compare material lock versions, not
  // raw legacy captures, so duplicate capture rows do not reintroduce noise.
  const newerLockVersionAvailable = useMemo(() => {
    return hasNewerMaterialRun(resolvedLockVersions, deliveryLockSelection?.lockVersionId ?? null);
  }, [deliveryLockSelection, resolvedLockVersions]);

  const [liveMaterialLockHash, setLiveMaterialLockHash] = useState<string | null>(null);
  const [liveMaterialLockHashLoaded, setLiveMaterialLockHashLoaded] = useState(false);

  useEffect(() => {
    const source = liveLoan ?? loan;
    let cancelled = false;

    if (source.rate_lock_status !== 'Locked') {
      setLiveMaterialLockHash(null);
      setLiveMaterialLockHashLoaded(true);
      return () => {
        cancelled = true;
      };
    }

    setLiveMaterialLockHashLoaded(false);
    computeMaterialLockHash(source as unknown as Record<string, unknown>)
      .then((hash) => {
        if (!cancelled) {
          setLiveMaterialLockHash(hash);
          setLiveMaterialLockHashLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLiveMaterialLockHash(null);
          setLiveMaterialLockHashLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [liveLoan, loan]);

  const [isApplyingActive, setIsApplyingActive] = useState(false);

  // Inspecting another material run remounts the workspace and would drop
  // unsaved edits silently; include entries typed while a save is sending,
  // even when they still match the old baseline.
  // A selection inside the same run (including normalising to null for the
  // active run) keeps the same run key and is allowed.
  const runIdForSelection = useCallback((id: string | null): string | null => {
    if (id === null) {
      return activeMaterialRun?.id ?? null;
    }
    return reviewableMaterialRuns.find((run) => run.snapshots.some((s) => s.id === id))?.id ?? id;
  }, [activeMaterialRun, reviewableMaterialRuns]);
  const handleLockVersionSelect = useCallback((id: string | null) => {
    if (
      parGuardEnabled
      && hasUnsavedEdits
      && runIdForSelection(id) !== runIdForSelection(resolvedSelectedLockVersionId)
    ) {
      toast.error('Save or discard your pricing changes first');
      return;
    }
    resolvedOnLockVersionSelect?.(id);
  }, [parGuardEnabled, resolvedSelectedLockVersionId, hasUnsavedEdits, resolvedOnLockVersionSelect, runIdForSelection]);

  const handleReviewLatest = useCallback(() => {
    const latestRun = reviewableMaterialRuns[0] ?? null;
    if (!latestRun || latestRun.hasActive) {
      return;
    }
    handleLockVersionSelect(latestRun.displayRepresentative.id);
    // Scroll the picker into view so the operator can see the new selection.
    requestAnimationFrame(() => {
      lockSectionRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [handleLockVersionSelect, reviewableMaterialRuns]);
  // Reset the cached lastSent line when the active selection changes (or is
  // cleared) — the value is keyed on selection.id + updated_at on the server,
  // so it isn't valid across selections.
  const deliveryLockSelectionKey = deliveryLockSelection
    ? `${deliveryLockSelection.id}::${deliveryLockSelection.updatedAt}`
    : null;
  useEffect(() => {
    setLockConfirmationLastSent(null);
  }, [deliveryLockSelectionKey]);
  const [priceFinalizedRecipients, setPriceFinalizedRecipients] = useState<PriceFinalizedPreviewRecipients>({
    to: [],
    cc: [],
    loading: false
  });
  const companyProfitBelowZero = recalc.companyProfitDollars < -0.005;

  useEffect(() => {
    if (!reasonModalOpen || priceFinalizedEmailSuppressed) {
      return;
    }
    let cancelled = false;
    setPriceFinalizedRecipients((prev) => ({ ...prev, loading: true }));

    async function loadRecipients() {
      const { data, error } = await supabase.functions.invoke('price-finalized-recipients');
      if (error) {
        throw error;
      }
      const to = Array.isArray(data?.to)
        ? data.to.filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        : [];
      const cc = Array.isArray(data?.cc)
        ? data.cc.filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        : [];
      if (!cancelled) {
        setPriceFinalizedRecipients({
          to,
          cc,
          loading: false
        });
      }
    }

    void loadRecipients().catch((error) => {
      console.warn('[ProductPricingWorkspace] Failed to load price finalized recipients', error);
      if (!cancelled) {
        setPriceFinalizedRecipients({ to: [], cc: [], loading: false });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [reasonModalOpen, priceFinalizedEmailSuppressed]);

  const priceFinalizedPreviewRows = useMemo<PriceFinalizedPreviewRow[]>(() => {
    return buildPriceFinalizedPreviewRows(baselineRecalc, recalc);
  }, [baselineRecalc, recalc]);

  const runSaveGuards = useCallback((): boolean => {
    // A finalized Banker Recon still takes an Investor Loan # on its own.
    if (isTerminalBankerRecon && pricingDirty) {
      toast.error('Pricing is locked. Reopen Banker Recon before making changes.');
      return false;
    }
    if (activeGroupBlocksNewSave || submittedSnapshotBlocksNewSave) {
      toast.error(QUEUED_SAVE_REASON);
      return false;
    }
    if (saveFieldsInvalid) {
      toast.error('Fix field errors before saving');
      return false;
    }
    if (investorOnlyDirty) {
      return true;
    }
    // Candidate mode: below par is the one message (profitability below $0
    // derives from the same rounded price and would otherwise win the race).
    if (isCandidateMode && recalc.belowPar) {
      toast.error('Below par — cover the shortfall first');
      return false;
    }
    if (companyProfitBelowZero) {
      toast.error('LO Profitability below $0 — fix shortfall to save');
      return false;
    }
    // Below-par direct guard — save is blocked when the displayed Net LO
    // Locked price is below 100.
    // The Save button disables and ⌘S won't fire, but any future programmatic
    // call path (toolbar action, retry-on-fix) bypasses those. Block here too,
    // before we even ask for a locked-loan reason.
    if (recalc.belowPar) {
      toast.error('Net LO Locked below par — fix shortfall to save');
      return false;
    }
    return true;
  }, [isTerminalBankerRecon, pricingDirty, investorOnlyDirty, activeGroupBlocksNewSave, submittedSnapshotBlocksNewSave, companyProfitBelowZero, saveFieldsInvalid, recalc.belowPar, isCandidateMode]);

  const showSaveError = useCallback((result: SaveResult) => {
    if (result.ok) {
      return;
    }
    // Known error codes → targeted copy; otherwise fall through to generic.
    const copy: Record<string, string> = {
      INVALID_REQUEST_SHAPE: 'Save rejected — invalid request shape.',
      UNAUTHORIZED: 'You are not signed in. Please refresh and try again.',
      ENCOMPASS_WRITES_DISABLED: 'Encompass writes are currently disabled.',
      FIELD_NOT_IN_ALLOWLIST: `Field ${result.fieldId ?? ''} is not in the write allowlist.`,
      FIELD_DISABLED: `Field ${result.fieldId ?? ''} is disabled.`,
      INVALID_NUMERIC: `Enter a valid number for ${result.fieldId ?? 'this field'}.`,
      NEGATIVE_NUMERIC: `${result.fieldId ?? 'Field'} must be non-negative.`,
      NO_ASSIGNED_LO: 'This loan has no assigned Loan Officer.',
      RESPA_DUAL_COMP: 'RESPA dual-comp — remove the LO comp margin in Encompass before editing BPC.',
      NO_COMP_PLAN_CONFIGURED: 'No comp plan configured for the assigned Loan Officer.',
      BPC_CAP_EXCEEDED: `Borrower-paid comp exceeds plan cap${result.capPercent ? ` of ${result.capPercent.toFixed(2)}%` : ''}.`,
      CHANGE_REASON_REQUIRED_FOR_LOCKED_LOAN: 'A reason is required to save pricing on a locked loan.',
      ACTIVE_GROUP_IN_FLIGHT: QUEUED_SAVE_REASON,
      INVESTOR_LOAN_SAVE_PAUSED: "Investor Loan # can't be saved right now. Enter it in Encompass or try again later.",
      INVALID_INVESTOR_LOAN_NUMBER: 'Enter a valid Investor Loan #.',
      INVESTOR_LOAN_NUMBER_TOO_LONG: 'Investor Loan # is too long for Encompass.',
      UNSUPPORTED_GROUP_FIELD: 'Save rejected — that field cannot be saved here.',
      PRICING_WRITE_IN_FLIGHT: `Another write is in flight for ${result.fieldId ?? 'this field'}.`,
      INVALID_PRICING_GROUP_PAYLOAD: 'Save rejected by server — check field values.',
      BANKER_RECON_FINALIZED: 'Pricing is locked. Reopen Banker Recon before making changes.',
      STAGING_FAILED: result.detail
        ? `Save rejected by server: ${result.detail}`
        : 'Save rejected by server. Please retry or contact support.',
      NOTHING_TO_SAVE: 'No pending changes to save.',
      NO_LO_COMP_BUILT_IN: 'No LO Compensation built into this price — LO comp reduction is unavailable.',
      LOCR_EXCEEDS_BUILT_IN_COMP: `LO comp reduction exceeds built-in LO compensation${
        typeof result.maxBps === 'number' ? ` (max ${result.maxBps} bps)` : ''
      }.`,
      LENDER_CREDIT_EXCEEDS_AVAILABLE: `Lender credit exceeds available${
        typeof result.maxGenericLenderCreditDollars === 'number'
          ? ` (max ${formatCurrency(result.maxGenericLenderCreditDollars)})`
          : ''
      }.`,
      LOAN_LOOKUP_FAILED: 'Couldn\'t load the loan — try again in a moment.',
      CONCESSIONS_QUERY_FAILED: 'Couldn\'t verify concessions — try again in a moment.',
      LOCR_LOOKUP_FAILED: 'Couldn\'t verify LO comp reduction — try again in a moment.',
      ENCOMPASS_LOAN_ID_MISMATCH: 'Loan reference mismatch — refresh the page and try again.',
      DELIVERY_LOCK_NOT_ACTIVE: 'Delivery lock changed. Refresh and try again.',
      DELIVERY_LOCK_LOOKUP_FAILED: 'Couldn\'t load delivery lock — try again in a moment.',
      LENDER_CREDIT_BELOW_PAR: 'Lender credit would put the loan below par — reduce it to save.',
      BELOW_PAR: 'Below par — cover the shortfall with discount points, an LO comp reduction, or a concession.',
      PAR_CHECK_UNAVAILABLE: 'Loan amount is missing, so par can\'t be checked.',
      FEATURE_DISABLED: 'Set Active guard is off — refresh the page.',
      LOCK_VERSION_NOT_FOUND: 'That lock version no longer exists. Refresh and try again.',
      LOCK_VERSION_NOT_SELECTABLE: 'That lock version is read-only and cannot be set active.',
      STALE_ACTIVE_SELECTION: 'Pricing changed under you — review and try again',
      PRICING_CHANGED: 'Pricing changed under you — review and try again',
      ENCOMPASS_READ_FAILED: "Couldn't read the loan's pricing from Encompass — try again.",
      ACTIVATION_TARGET_MISMATCH: 'This pending save targets a different lock. Refresh and try again.',
      CANCEL_BLOCKED_ACTIVATION: 'This save switched the active lock, so it can\'t be cancelled. It will update Encompass when the loan is free.'
    };
    toast.error(copy[result.error] ?? `Save rejected — ${result.error}`);
  }, []);

  const submitPricingSave = useCallback(async (
    changeReason?: string,
    opts: { priceFinalizedEmailRequested?: boolean } = {}
  ): Promise<boolean> => {
    if (!runSaveGuards()) {
      return false;
    }
    const normalizedReason = changeReason?.trim();
    if (loan.rate_lock_status === 'Locked' && !normalizedReason && !investorOnlyDirty) {
      toast.error('A reason is required to save pricing on a locked loan.');
      return false;
    }

    const fields = draft.buildSavePayload();
    const submittedValues = draft.dirtyFields.reduce<Partial<Record<PricingFieldId, string>>>((acc, id) => {
      acc[id] = draft.values[id].value;
      return acc;
    }, {});
    // The Investor Loan # is written to the loan when staged, so it never waits
    // in the pricing readback snapshot (its row update must not release one).
    const { investor_loan_number: _submittedInvestorLoanNumber, ...snapshotValues } = submittedValues;
    validatedToastCopyRef.current = investorOnlyDirty
      ? 'Investor Loan # sent to Encompass'
      : 'Pricing sent to Encompass';
    const submittedOverrides = buildSubmittedRecalcOverrides(fields);
    const hasEncompassField = fields.some((f) => f.fieldId !== 'LW.LO_REDUCTION');
    const saveStartedAt = new Date().toISOString();

    const markCommitted = (groupId: string) => {
      if (Object.keys(snapshotValues).length > 0) {
        setSubmittedSnapshot({
          groupId,
          startedAt: saveStartedAt,
          values: snapshotValues,
          overrides: submittedOverrides,
          encompassKeyAtStart: encompassAuthoritativeKey,
          locrKeyAtStart: locrAuthoritativeKey
        });
      }
      if (Object.keys(submittedValues).length > 0) {
        draft.rebaseSubmitted(submittedValues);
      }
    };

    if (isCandidateMode) {
      suppressValidatedToastRef.current = true;
    }
    // Rebases while this save is on the wire (realtime brings staging's own
    // write, or pricing items finish loading) keep the sent fields as typed.
    // The reply ends the marks: accepted ones become the saved baseline.
    draft.beginSubmission(submittedValues);
    let submissionOpen = true;
    try {
      const result = await grouped.save(
        isCandidateMode && candidateVersion
          ? {
              fields,
              changeReason: normalizedReason,
              priceFinalizedEmailRequested: false,
              // Set Active & Save: the server validates against this lock
              // version, stages the write and switches the active lock in one
              // transaction. The expected selection and the revision values
              // let it refuse when the screen is stale.
              targetLockVersionId: candidateVersion.id,
              expectedActiveLockVersionId: deliveryLockSelection?.lockVersionId ?? null,
              revision: {
                loanUpdatedAt: (liveLoan ?? loan).updated_at ?? null,
                locrBps: locrBaseline === null ? null : toNumberOrNull(locrBaseline) ?? 0,
                concessionPoints: concessions
                  .filter((c) => !c.is_legacy)
                  .reduce((sum, c) => sum + (Number(c.amount_points) || 0), 0)
              }
            }
          : {
              fields,
              changeReason: normalizedReason,
              priceFinalizedEmailRequested: investorOnlyDirty ? false : opts.priceFinalizedEmailRequested ?? false,
              // Active delivery-lock saves: server-side validation reads the
              // selection's `effective_rate_lock_fields` (margin stack, base price,
              // base loan amount) instead of live `encompass_loans` lock fields.
              // Keeps client and server validation aligned with the lock the user
              // is actually editing against.
              deliveryLockSelectionId: isDeliveryLockMode ? deliveryLockSelection?.id ?? null : null
            }
      );
      if (result.ok) {
        if (result.status !== 'validated') {
          suppressValidatedToastRef.current = false;
        }
        markCommitted(result.groupId);
        submissionOpen = false;
        await refreshAuthoritativePricing();
        await refreshPricingItems();
        if (isCandidateMode) {
          // One toast per outcome; the generic validated toast was suppressed.
          switch (result.status) {
            case 'validated':
              toast.success(hasEncompassField
                ? 'Saved to Encompass. Active delivery lock updated. No lock-confirmation email sent.'
                : 'Saved. Active delivery lock updated. No lock-confirmation email sent.');
              break;
            case 'waiting_unlock':
              toast.info(activationWaitingLabel);
              break;
            case 'waiting_retry':
              toast.info('Saved here. Active delivery lock updated — retrying Encompass sync', {
                description: result.retryingFieldIds?.length
                  ? `Fields retrying: ${result.retryingFieldIds.join(', ')}`
                  : undefined
              });
              break;
          }
          return true;
        }
        switch (result.status) {
          case 'validated':
            // onValidated fires its own toast. The draft stays in a pending
            // submitted state while the authoritative baseline waits for
            // Encompass readback/local pricing refresh.
            break;
          case 'waiting_unlock':
            toast.info(investorOnlyDirty ? 'Saved in the portal — waiting for Encompass unlock' : 'Saved — waiting for Encompass unlock', {
              description: result.lockedFieldIds?.length
                ? `Fields locked: ${result.lockedFieldIds.join(', ')}`
                : undefined
            });
            break;
          case 'waiting_retry':
            toast.info('Saved — retrying Encompass sync', {
              description: result.retryingFieldIds?.length
                ? `Fields retrying: ${result.retryingFieldIds.join(', ')}`
                : undefined
            });
            break;
        }
        return true;
      }
      if (result.error === 'SAVE_IN_FLIGHT') {
        // An earlier save from this tab is still on the wire and owns the
        // marks. These values were not sent, so they stay pending.
        submissionOpen = false;
        return false;
      }
      suppressValidatedToastRef.current = false;
      if (result.recoveredGroup) {
        // The response was lost after the server committed the group: the
        // submitted values are saved and the bar tracks the group.
        markCommitted(result.recoveredGroup.id);
        submissionOpen = false;
        await refreshAuthoritativePricing();
        await refreshPricingItems();
        toast.info('Save recorded — waiting for Encompass status');
        return true;
      }
      // Refused: release the marks now, before any later save can set its own.
      draft.endSubmission();
      submissionOpen = false;
      if (result.error === 'STALE_ACTIVE_SELECTION' || result.error === 'PRICING_CHANGED') {
        await refreshAuthoritativePricing();
        await refreshPricingItems();
        await refreshConcessions();
      }
      showSaveError(result);
      return false;
    } finally {
      if (submissionOpen) {
        // The request threw before it settled.
        draft.endSubmission();
      }
      if (isCandidateMode && candidateVersion && onActivated) {
        // Re-read the active selection whatever happened: a 500 can follow a
        // committed activation. The parent leaves candidate mode only when
        // this target is confirmed active, so a rejection keeps the draft.
        try {
          await onActivated(candidateVersion.id);
        } catch (error) {
          console.warn('[ProductPricingWorkspace] Failed to re-read the active delivery lock', error);
        }
      }
    }
  }, [
    draft,
    grouped,
    loan,
    liveLoan,
    encompassAuthoritativeKey,
    locrAuthoritativeKey,
    locrBaseline,
    concessions,
    refreshAuthoritativePricing,
    refreshPricingItems,
    refreshConcessions,
    runSaveGuards,
    showSaveError,
    isDeliveryLockMode,
    isCandidateMode,
    candidateVersion,
    deliveryLockSelection,
    onActivated,
    activationWaitingLabel,
    investorOnlyDirty
  ]);

  const handleSetActiveFromLockVersion = useCallback(async (version: LockVersion) => {
    if (!resolvedOnUseLockVersionAsDeliveryLock) {
      return;
    }
    if (isApplyingActive) {
      return;
    }
    if (isTerminalBankerRecon) {
      toast.error('Pricing is locked. Reopen Banker Recon before changing the delivery lock.');
      return;
    }
    if (hasUnsavedEdits) {
      toast.error(isCandidateMode
        ? 'Pricing changes apply with Set Active & Save'
        : 'Save pricing changes before setting active');
      return;
    }
    if (allBlockedReason) {
      toast.error(allBlockedReason);
      return;
    }
    // Below par blocks the displayed candidate only; every other target —
    // including the live lock's rows when no lock is active — is left to the
    // server, which judges that lock's own economics. (The picker already
    // disables the header button for the displayed lock.)
    const targetsDisplayedLock = isCandidateMode && version.id === candidateVersion?.id;
    if (headerBlockedReason && targetsDisplayedLock) {
      toast.error(headerBlockedReason);
      return;
    }
    setIsApplyingActive(true);
    try {
      // Parent (LoanDetailsSheet.handleUseLockVersionAsDeliveryLock) owns the
      // service call, state update, setSelectedLockVersionId(null), AND success
      // toast. The wrapper here only manages busy state; the selection-key
      // effect resets lockConfirmationLastSent on a real change. Error path
      // stays here so the operator sees failures even when the parent does
      // its own state update.
      await resolvedOnUseLockVersionAsDeliveryLock(version);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to apply delivery lock');
    } finally {
      setIsApplyingActive(false);
    }
  }, [
    resolvedOnUseLockVersionAsDeliveryLock,
    isApplyingActive,
    isTerminalBankerRecon,
    hasUnsavedEdits,
    isCandidateMode,
    candidateVersion,
    allBlockedReason,
    headerBlockedReason
  ]);

  const handleSave = useCallback(async () => {
    if (!runSaveGuards()) {
      return;
    }
    if (loan.rate_lock_status === 'Locked' && pricingDirty) {
      setReasonDraft('');
      setPriceFinalizedEmailRequested(true);
      setReasonModalOpen(true);
      return;
    }
    await submitPricingSave();
  }, [loan.rate_lock_status, pricingDirty, runSaveGuards, submitPricingSave]);

  const handleReasonModalOpenChange = useCallback((open: boolean) => {
    setReasonModalOpen(open);
    if (!open) {
      setReasonDraft('');
      setPriceFinalizedEmailRequested(true);
    }
  }, []);

  const handleReasonSubmit = useCallback(async (reason: string) => {
    // Force the price-finalized email off in delivery-lock mode regardless of
    // state. The toggle is hidden in DL mode (`showPriceFinalizedEmail` flag),
    // but the underlying state defaults to `true` and would otherwise fire the
    // email through the normal grouped save path. Belt-and-suspenders gate.
    const saved = await submitPricingSave(reason, {
      priceFinalizedEmailRequested: priceFinalizedEmailSuppressed ? false : priceFinalizedEmailRequested
    });
    if (saved) {
      setReasonModalOpen(false);
      setReasonDraft('');
      setPriceFinalizedEmailRequested(true);
    }
  }, [priceFinalizedEmailSuppressed, priceFinalizedEmailRequested, submitPricingSave]);

  const handleRetry = useCallback(async () => {
    if (!grouped.activeGroup) {
      return;
    }
    const result = await grouped.retry(grouped.activeGroup.id);
    if (result.ok) {
      await refreshAuthoritativePricing();
      await refreshPricingItems();
      switch (result.status) {
        case 'validated':
          break;
        case 'waiting_unlock':
          toast.info('Still waiting for the Encompass file to unlock', {
            description: result.lockedFieldIds?.length
              ? `Fields locked: ${result.lockedFieldIds.join(', ')}`
              : undefined
          });
          break;
        case 'waiting_retry':
          toast.info('Retry queued — Encompass sync will try again', {
            description: result.retryingFieldIds?.length
              ? `Fields retrying: ${result.retryingFieldIds.join(', ')}`
              : undefined
          });
          break;
      }
      return;
    }

    const copy: Record<string, string> = {
      GROUP_NOT_FOUND: 'The pending pricing save no longer exists.',
      GROUP_NOT_RETRYABLE: 'This pricing save cannot be retried in its current state.',
      GROUP_HAS_NO_AUDITS: 'This pricing save has no staged changes to retry.',
      RETRY_GROUP_LOOKUP_FAILED: 'Retry failed while loading the pending save.',
      RETRY_AUDIT_LOOKUP_FAILED: 'Retry failed while loading staged changes.',
      ACTIVE_GROUP_IN_FLIGHT: 'Another pricing save is still finishing for this loan.',
      ENCOMPASS_WRITES_DISABLED: 'Encompass writes are currently disabled.',
      UNAUTHORIZED: 'You are not signed in. Please refresh and try again.'
    };
    toast.error(copy[result.error] ?? `Retry failed — ${result.error}`);
  }, [grouped, refreshAuthoritativePricing, refreshPricingItems]);

  const handleCancel = useCallback(async () => {
    if (!grouped.activeGroup) {
      return;
    }
    const result = await grouped.cancel(grouped.activeGroup.id);
    if (result.ok) {
      toast.info('Pricing save cancelled — staged changes reverted');
      // Nothing is pending readback any more: the submitted snapshot would
      // otherwise wait for a pricing-value change the revert never produces.
      setSubmittedSnapshot(null);

      // Translate reverted field values into draft-unit strings so the aside
      // reflects the server-cancelled state while the parent refreshes
      // encompass_loans. The Investor Loan # was written to the loan row when
      // staged, and the database holds that number while the group can be
      // cancelled, so the server always restores the previous one. Rebasing
      // from this callback's loan would bring the cancelled number back.
      const draftOverrides: Partial<Record<PricingFieldId, string>> = {};
      for (const r of result.reverted ?? []) {
        const n = r.previousLocalValue === null || r.previousLocalValue === ''
          ? null
          : Number(r.previousLocalValue);
        const numeric = n !== null && Number.isFinite(n) ? n : null;
        switch (r.fieldId) {
          case 'NEWHUD.X1150':
            draftOverrides.discount_point_percent = numeric === null ? '' : numeric.toFixed(3);
            break;
          case '388':
            draftOverrides.origination_fee_percent =
              numeric === null ? '' : formatPercentInputValue(numeric);
            break;
          case '4794':
            draftOverrides.lender_credit_4794_dollars =
              r.previousLocalValue === null || r.previousLocalValue === ''
                ? ''
                : formatDollarInputValue(String(r.previousLocalValue));
            break;
          case 'LW.LO_REDUCTION':
            draftOverrides.lo_comp_reduction_bps =
              numeric === null ? '' : formatBpsInputValue(numeric);
            break;
          case '352':
            draftOverrides.investor_loan_number = (r.previousLocalValue ?? '').trim();
            break;
        }
      }
      await refreshAuthoritativePricing();
      await refreshPricingItems();
      if (Object.keys(draftOverrides).length > 0) {
        draft.rebaseFromValues(draftOverrides);
      } else {
        draft.rebaseClean(draftSourceLoan, pricingItems);
      }
    } else if (result.error === 'ALREADY_PUSHED') {
      toast.error('Can\'t cancel — the save already pushed to Encompass.');
    } else if (result.error === 'CANCEL_BLOCKED_ACTIVATION') {
      toast.error('This save switched the active lock, so it can\'t be cancelled. It will update Encompass when the loan is free.');
    } else {
      toast.error(`Cancel failed — ${result.error ?? 'unknown error'}`);
    }
  }, [draft, grouped, draftSourceLoan, pricingItems, refreshAuthoritativePricing, refreshPricingItems]);

  // One reason drives the Save button, its tip and ⌘S. A save with only the
  // Investor Loan # is held back only by another save in flight or its own
  // entry error; pricing rules apply when a pricing field changed.
  const saveBlockedReason: string | null = (() => {
    if (isTerminalBankerRecon && pricingDirty) {
      return 'Reopen Banker Recon before changing pricing.';
    }
    if (activeGroupBlocksNewSave || submittedSnapshotBlocksNewSave) {
      return QUEUED_SAVE_REASON;
    }
    if (saveFieldsInvalid) {
      return 'Fix field errors before saving';
    }
    if (investorOnlyDirty) {
      return null;
    }
    if (isCandidateMode && recalc.belowPar) {
      return 'Below par — cover the shortfall first';
    }
    if (companyProfitBelowZero) {
      return 'LO Profitability below $0 — fix shortfall to save';
    }
    if (recalc.belowPar) {
      return 'Net LO Locked below par — fix shortfall to save';
    }
    if (draft.dirtyFields.includes('origination_fee_percent') && !bpcCap.allowEdit) {
      return bpcCap.reason;
    }
    return null;
  })();
  const saveDisabled = saveBlockedReason !== null;

  // 2B.15 keyboard shortcuts — scoped to the workspace while the bar is visible.
  const displayActiveGroup = grouped.activeGroup ?? (
    submittedSnapshot
      ? { id: submittedSnapshot.groupId, status: 'readback_pending', createdAt: submittedSnapshot.startedAt }
      : null
  );
  // DL saves now go through grouped.save (same pipeline as live saves), so the
  // operator needs the same status / cancel / retry UI in DL mode. Snapshot
  // mode is the only branch that suppresses the bar (read-only historical).
  const showPendingBar = !isViewingSnapshot && (draft.dirtyCount > 0 || displayActiveGroup !== null);
  // Status-only mode = bar visible but no editable diff (post-commit waiting
  // for unlock/retry, or briefly success). Never show drawer or reserve
  // .review-open padding here — the drawer would have nothing to render.
  const statusOnly = draft.dirtyCount === 0 && displayActiveGroup !== null;
  // A real dirty session: bar visible AND the user has unsaved field edits.
  // The drawer auto-opens on the rising edge of this — not on `showPendingBar`
  // alone, which would also fire on a clean → status-only transition.
  const dirtySessionOpen = showPendingBar && !statusOnly && draft.dirtyCount > 0;

  // Pending-bar review drawer state. Single source of truth: the workspace
  // owns this and passes `reviewOpen` + `onReviewToggle` to the (controlled)
  // bar. Default-open per product decision: when a dirty session begins, the
  // drawer is what makes the bar useful, so show it without an extra click.
  const [reviewOpen, setReviewOpen] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    if (!isTerminalBankerRecon) {
      return;
    }
    setManageOpen(false);
    setReasonModalOpen(false);
  }, [isTerminalBankerRecon]);

  // Falling edge: collapse the drawer whenever the bar shouldn't be expanded
  // (snapshot mode dropped showPendingBar, fully discarded, or status-only).
  useEffect(() => {
    if (!showPendingBar || statusOnly) {
      setReviewOpen(false);
    }
  }, [showPendingBar, statusOnly]);

  // Rising edge: open the drawer when a fresh dirty session begins. Manual
  // "Hide" within the same session is preserved (this only fires on the
  // false → true transition of dirtySessionOpen).
  const prevDirtySessionOpen = useRef(dirtySessionOpen);
  useEffect(() => {
    if (dirtySessionOpen && !prevDirtySessionOpen.current) {
      setReviewOpen(true);
    }
    prevDirtySessionOpen.current = dirtySessionOpen;
  }, [dirtySessionOpen]);

  useEffect(() => {
    if (!showPendingBar) {
return;
}
    const handler = (e: KeyboardEvent) => {
      if (reasonModalOpen) {
        return;
      }
      // Escape collapses the drawer regardless of focus target — typing in
      // a pricing input must not block "Esc closes review". Capture-phase
      // registration + stopPropagation keeps Radix Sheet from also closing.
      if (e.key === 'Escape' && reviewOpen) {
        e.preventDefault();
        e.stopPropagation();
        setReviewOpen(false);
        return;
      }
      const target = e.target as HTMLElement | null;
      // Don't hijack typing inside inputs / textareas for ⌘S etc.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
return;
}
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        // Same rule as the Save button, which is disabled while a save runs.
        if (draft.dirtyCount > 0 && !saveDisabled && !grouped.isSaving) {
          void handleSave();
        }
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [showPendingBar, reviewOpen, reasonModalOpen, draft, handleSave, saveDisabled, grouped.isSaving]);

  // Centering — write the workspace's center-x to a CSS variable that the
  // pending bar's `left:` consumes. `position: fixed` would otherwise center
  // to the viewport, which is offset by the LoanDetailsSheet's right-anchor
  // (92vw / 1600px max) and the 192px tab sidebar inside the sheet.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const updateCenter = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--pv2-bar-center-x', `${rect.left + rect.width / 2}px`);
  }, []);
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    updateCenter();
    window.addEventListener('resize', updateCenter);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateCenter);
      ro.observe(el);
    }
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updateCenter);
    };
  }, [updateCenter]);
  // Recompute on bar reveal — sheet's slide-in is 500ms, so a single rAF can
  // fire mid-animation. Three-stage cadence catches initial / settled / done.
  useEffect(() => {
    if (!showPendingBar) {
      return;
    }
    updateCenter();
    const raf = requestAnimationFrame(updateCenter);
    const t = window.setTimeout(updateCenter, 550);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [showPendingBar, updateCenter]);

  // A save that switched the active lock is never cancellable (the lock has
  // already moved); it waits for the Encompass write like any grouped save.
  const cancelEligible =
    grouped.activeGroup !== null
    && !activeGroupSwitchedLock
    && ['pending', 'waiting_unlock', 'waiting_retry'].includes(grouped.activeGroup.status);
  const retryEligible =
    grouped.activeGroup !== null
    && (
      ['waiting_unlock', 'waiting_retry'].includes(grouped.activeGroup.status)
      || (grouped.activeGroup.status === 'pending' && pendingStale)
    );

  const wrapperClasses = [
    showPendingBar ? 'has-pending-bar' : '',
    showPendingBar && !statusOnly && reviewOpen ? 'review-open' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={wrapperRef} data-pricing-v2 className={wrapperClasses}>
      <div className="pv2-workspace">
        <div className="pv2-content">
          {isTerminalBankerRecon && (
            <Alert className="border-warning/50 bg-warning/10 text-foreground">
              <LockKeyhole className="h-4 w-4 text-warning" aria-hidden="true" />
              <AlertTitle>Pricing locked by Banker Recon</AlertTitle>
              <AlertDescription>
                This Banker Recon is {reconStatus}. Reopen it before changing pricing, lock versions, or the active delivery lock.
              </AlertDescription>
            </Alert>
          )}
          {inspecting && (
            <LockVersionBanner
              version={selectedLockVersion}
              candidate={isCandidateMode}
              belowParLine={belowParLine}
            />
          )}
          {!isViewingSnapshot && isDeliveryLockMode && deliveryLockSelection && (
            <DeliveryLockBanner
              selection={deliveryLockSelection}
              displayLabel={activeMaterialRun?.displayRepresentative.label ?? null}
              displayCapturedAt={activeMaterialRun?.displayRepresentative.capturedAt ?? null}
              onSendConfirmation={loan.encompass_loan_id ? () => setConfirmLockEmailOpen(true) : undefined}
              pricingDirty={draft.dirtyCount > 0}
              lastSent={lockConfirmationLastSent}
              newerLockVersionAvailable={newerLockVersionAvailable}
              onReviewLatest={newerLockVersionAvailable ? handleReviewLatest : undefined}
            />
          )}

          <LockStatusCard loan={loan} />

          <div className="lock-section" ref={lockSectionRef}>
            <LockHistoryPicker
              lockVersions={resolvedLockVersions}
              selectedLockVersionId={resolvedSelectedLockVersionId}
              onLockVersionSelect={handleLockVersionSelect}
              isViewingSnapshot={inspecting}
              activeLockVersionId={deliveryLockSelection?.lockVersionId ?? null}
              isApplyingActive={isApplyingActive}
              pricingDirty={hasUnsavedEdits}
              dirtyTitle={isCandidateMode ? 'Pricing changes apply with Set Active & Save' : undefined}
              headerBlockedReason={headerBlockedReason}
              allBlockedReason={allBlockedReason}
              loanIsLocked={(liveLoan ?? loan).rate_lock_status === 'Locked'}
              liveLockFields={liveLoan ?? loan}
              liveMaterialLockHash={liveMaterialLockHash}
              liveMaterialLockHashLoaded={liveMaterialLockHashLoaded}
              onSetActiveFromLockVersion={
                resolvedOnUseLockVersionAsDeliveryLock && !isTerminalBankerRecon
                  ? handleSetActiveFromLockVersion
                  : undefined
              }
            />
            <LockDetailsCard
              loan={loan}
              currentLoan={currentLoan}
              diffClass={diffClass}
              investorLoanNumber={
                isViewingSnapshot && !isCandidateMode
                  ? (loan.rate_lock_investor_commitment ?? '')
                  : draft.values.investor_loan_number.value
              }
              investorLoanNumberError={inspecting ? null : draft.values.investor_loan_number.invalid}
              investorLoanNumberReadOnly={inspecting}
              onInvestorLoanNumberChange={(value) => draft.setValue('investor_loan_number', value)}
            />
            {!inspecting && (
              <InvestorLockDocsCard encompassLoanId={loan.encompass_loan_id} isViewingSnapshot={inspecting} />
            )}
          </div>

          <LoanContextRow loan={loan} />

          <KpiStrip
            recalc={recalc}
            loCompensationDollars={recalc.loCompensationDollars}
            netToLendwiseDollars={recalc.netToLendwiseDollars}
          />

          <PriceFlowDiagram recalc={recalc} />

          <PricingBreakdownLedger loan={loan} loanAmount={recalc.loanAmount} />

          <PriceWaterfallLedger loan={loan} recalc={recalc} />

          <ConcessionsStrip
            loan={loan}
            concessions={concessions}
            cureDollars={toNumberOrNull(loan.cure_applied_lender_credit) ?? 0}
            lenderCreditDollars={recalc.lenderCreditDollars}
            isViewingSnapshot={isViewingSnapshot || isTerminalBankerRecon}
            onManage={!isViewingSnapshot && !isTerminalBankerRecon ? () => setManageOpen(true) : undefined}
          />

          <PricingChangeHistoryPanel encompassLoanId={loan.encompass_loan_id} />
        </div>

        <div className="pv2-aside">
          <PricingControlsAside
            recalc={recalc}
            pricingItems={pricingItems}
            draft={draft}
            bpcCap={bpcCap}
            locrEligibility={locrEligibility}
            isViewingSnapshot={isViewingSnapshot || isTerminalBankerRecon}
            builtInLoCompBps={builtInLoCompBps}
            baselineNetToLendwiseDollars={baselineRecalc.netToLendwiseDollars}
            parShortfall={parShortfall}
            setActivePending={setActivePending}
          />
        </div>
      </div>

      {showPendingBar && (
        <PricingPendingBar
          draft={draft}
          recalc={recalc}
          baselineRecalc={baselineRecalc}
          onSave={() => {
            void handleSave();
          }}
          onRetry={retryEligible ? () => {
            void handleRetry();
          } : undefined}
          onCancel={grouped.activeGroup && !activeGroupSwitchedLock ? () => {
            void handleCancel();
          } : undefined}
          primaryLabel={isCandidateMode ? 'Set Active & Save' : undefined}
          waitingUnlockLabel={
            activeGroupSwitchedLock
              ? activationWaitingLabel
              : activeGroupInvestorOnly
                ? investorWaitingLabel
                : undefined
          }
          reviewOpen={reviewOpen}
          onReviewToggle={setReviewOpen}
          isSaving={grouped.isSaving}
          isCancelling={grouped.isCancelling}
          saveDisabled={saveDisabled}
          saveDisabledReason={saveBlockedReason ?? undefined}
          activeGroup={displayActiveGroup}
          cancelEligible={cancelEligible}
          retryEligible={retryEligible}
        />
      )}
      <ConcessionsCureModal
        open={manageOpen}
        onOpenChange={setManageOpen}
        loan={loan}
        concessions={concessions}
        basePriceLocked={recalc.basePriceLocked}
        loanAmount={recalc.loanAmount}
        onLoanUpdate={onLoanUpdate}
        refreshConcessions={refreshConcessions}
        netLoLocked={parGuardEnabled ? baselineRecalc.netLoLocked : null}
        deleteBlockedReason={concessionDeleteBlockedReason}
      />
      <PricingReasonModal
        open={reasonModalOpen}
        reason={reasonDraft}
        isSaving={grouped.isSaving}
        priceFinalizedEmailRequested={priceFinalizedEmailRequested}
        onPriceFinalizedEmailRequestedChange={setPriceFinalizedEmailRequested}
        priceFinalizedPreview={{
          recipients: priceFinalizedRecipients,
          rows: priceFinalizedPreviewRows
        }}
        showPriceFinalizedEmail={!priceFinalizedEmailSuppressed}
        onOpenChange={handleReasonModalOpenChange}
        onReasonChange={setReasonDraft}
        onSubmit={(reason) => {
          void handleReasonSubmit(reason);
        }}
      />
      {loan.encompass_loan_id && (
        <ConfirmLockEmailModal
          open={confirmLockEmailOpen}
          encompassLoanId={loan.encompass_loan_id}
          onOpenChange={setConfirmLockEmailOpen}
          onLastSentChange={setLockConfirmationLastSent}
        />
      )}
    </div>
  );
}
